import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type Options = {
  /** Whether this slide is the one filling the frame. */
  active: boolean;
  /** Owned by the feed, so unmuting once stays unmuted for the whole session. */
  muted: boolean;
  /** False while the slide is outside the preload window and holds no source. */
  loaded: boolean;
};

export type ReelVideo = {
  videoRef: RefObject<HTMLVideoElement | null>;
  /** True while the element is actually playing, not merely asked to. */
  playing: boolean;
  /**
   * The browser refused to play even muted, or the visitor paused it. Either
   * way the slide draws its poster and a play control, and nothing retries.
   */
  blocked: boolean;
  /** 0–1 through the clip, updated once per frame while playing. */
  progress: number;
  togglePlay: () => void;
};

/**
 * One clip in the feed: when it plays, when it stops, and how far through it is.
 *
 * ## Autoplay, and giving up on it
 *
 * `play()` returns a promise that rejects when the browser declines, and every
 * browser declines an unmuted autoplay before the visitor has interacted with
 * the page. The feed therefore starts muted — but a visitor who has unmuted
 * one clip then swipes to the next, and that next clip is an unmuted autoplay
 * on an element the visitor has never touched, which some browsers still
 * refuse. So a refusal falls back to muted and tries exactly once more.
 *
 * Once, and counted in a ref. Retrying a blocked `play()` on every rejection
 * is an infinite loop that pins a CPU core and empties a battery, and it is
 * the single most common way this component is written wrong. After the one
 * retry the slide shows its poster and a play button, which is a working
 * state and not a failure.
 *
 * ## Leaving a slide stops it dead
 *
 * Pausing is not enough: a clip left paused four slides back is still holding
 * a decoder, and on a phone there are only a handful of those. It is paused
 * and rewound, so the next visit starts at the beginning the way a feed
 * should.
 *
 * ## Reduced motion
 *
 * Nothing autoplays. A silent video that starts by itself is exactly the
 * motion that setting exists to refuse; the clip is still there, with its
 * poster and its play button, for anyone who wants it.
 */
export function useReelVideo({ active, muted, loaded }: Options): ReelVideo {
  const videoRef = useRef<HTMLVideoElement>(null);
  const retried = useRef(false);
  const frame = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [progress, setProgress] = useState(0);
  const reducedMotion = usePrefersReducedMotion();

  /** Plays, and on a refusal falls back to muted for one single retry. */
  const attemptPlay = useCallback((element: HTMLVideoElement) => {
    element.play().catch(() => {
      if (retried.current) {
        setBlocked(true);
        return;
      }
      retried.current = true;
      element.muted = true;
      element.play().catch(() => setBlocked(true));
    });
  }, []);

  useEffect(() => {
    const element = videoRef.current;
    if (!element || !loaded) return;

    if (active && !reducedMotion) {
      retried.current = false;
      setBlocked(false);
      attemptPlay(element);
      return;
    }

    element.pause();
    // Rewound, not merely paused: see above.
    element.currentTime = 0;
    setProgress(0);
  }, [active, loaded, reducedMotion, attemptPlay]);

  // Muting is a property and not an attribute as far as the DOM is concerned,
  // so React's `muted` prop does not reliably reach an element that is already
  // playing. It is set here, where it can be.
  useEffect(() => {
    const element = videoRef.current;
    if (element) element.muted = muted;
  }, [muted]);

  /**
   * Progress on a frame loop rather than from `timeupdate`.
   *
   * `timeupdate` fires about four times a second, which on a thin progress bar
   * is a visible four-step stutter. The loop runs only while this slide is
   * playing — one slide in the whole feed — and stops the moment it is not.
   */
  useEffect(() => {
    const element = videoRef.current;
    if (!element || !playing) return;

    const tick = () => {
      const duration = element.duration;
      if (Number.isFinite(duration) && duration > 0) {
        setProgress(Math.min(element.currentTime / duration, 1));
      }
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [playing]);

  const togglePlay = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;
    if (element.paused) {
      // An explicit press is an interaction, so the one-retry budget is fresh.
      retried.current = false;
      setBlocked(false);
      attemptPlay(element);
    } else {
      element.pause();
      setBlocked(true);
    }
  }, [attemptPlay]);

  const onPlay = useCallback(() => {
    setPlaying(true);
    setBlocked(false);
  }, []);
  const onPause = useCallback(() => setPlaying(false), []);

  // Attached as DOM listeners rather than React props so that a `play()` the
  // browser starts on its own — a source swap, a return from background — is
  // seen too.
  useEffect(() => {
    const element = videoRef.current;
    if (!element) return;
    element.addEventListener('play', onPlay);
    element.addEventListener('pause', onPause);
    return () => {
      element.removeEventListener('play', onPlay);
      element.removeEventListener('pause', onPause);
    };
  }, [onPlay, onPause, loaded]);

  return { videoRef, playing, blocked, progress, togglePlay };
}
